import _ from "lodash/fp.js";
import { useState, useRef, useCallback, useEffect } from "react";
const computePopupPosition = (args) => {
  const { elementSize, gap, preferredSide, targetPosition, viewportSize } = args;
  const getPosition = (s) => {
    const left = _.flow(_.clamp(0, viewportSize.width - elementSize.width), _.clamp(targetPosition.left - elementSize.width + 16, targetPosition.right - 16))((targetPosition.left + targetPosition.right) / 2 - elementSize.width / 2);
    const top = _.flow(_.clamp(0, viewportSize.height - elementSize.height), _.clamp(targetPosition.top - elementSize.height + 16, targetPosition.bottom - 16))((targetPosition.top + targetPosition.bottom) / 2 - elementSize.height / 2);
    switch (s) {
      case "top":
        return { top: targetPosition.top - elementSize.height - gap, left };
      case "right":
        return { left: targetPosition.right + gap, top };
      case "bottom":
        return { top: targetPosition.bottom + gap, left };
      case "left":
        return { left: targetPosition.left - elementSize.width - gap, top };
      default:
        throw new Error("Invalid side");
    }
  };
  const position = getPosition(preferredSide);
  const maybeFlip = (d) => {
    switch (d) {
      case "top":
        return position.top < 0 ? "bottom" : "top";
      case "right":
        return position.left + elementSize.width >= viewportSize.width ? "left" : "right";
      case "bottom":
        return position.top + elementSize.height >= viewportSize.height ? "top" : "bottom";
      case "left":
        return position.left < 0 ? "right" : "left";
      default:
        throw new Error("Invalid side");
    }
  };
  const finalSide = maybeFlip(preferredSide);
  const finalPosition = getPosition(finalSide);
  return {
    position: {
      ...finalPosition,
      right: finalPosition.left + elementSize.width,
      bottom: finalPosition.top + elementSize.height
    },
    side: finalSide
  };
};
const toBoundingRect = _.pick([
  "width",
  "height",
  "top",
  "right",
  "bottom",
  "left"
]);
const useBoundingRects = (selectors) => {
  const [dimensions, setDimensions] = useState(selectors.map(() => ({ width: 0, height: 0, top: 0, right: 0, bottom: 0, left: 0 })));
  const dimensionsRef = useRef();
  dimensionsRef.current = dimensions;
  const selectorsRef = useRef(selectors);
  selectorsRef.current = selectors;
  const animationRef = useRef();
  const computePosition = useCallback(() => {
    const newDimensions = selectorsRef.current.map((selector) => {
      if ("ref" in selector && selector.ref.current) {
        return toBoundingRect(selector.ref.current.getBoundingClientRect());
      }
      if ("id" in selector) {
        const element = document.getElementById(selector.id);
        if (element) {
          return toBoundingRect(element.getBoundingClientRect());
        }
      }
      if ("viewport" in selector) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        return { width, height, top: 0, right: width, bottom: height, left: 0 };
      }
      return { width: 0, height: 0, top: 0, right: 0, bottom: 0, left: 0 };
    });
    if (!_.isEqual(newDimensions, dimensionsRef.current)) {
      setDimensions(newDimensions);
    }
    animationRef.current = requestAnimationFrame(computePosition);
  }, []);
  useEffect(() => {
    computePosition();
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [computePosition]);
  return dimensions;
};
export {
  computePopupPosition,
  useBoundingRects
};
